import { useCallback, useEffect, useId, useRef, useState } from "react";
import type {
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  RefObject,
} from "react";

import type { CaseSummary, ConversationSummary, Locale } from "../../../shared/types";
import type { Respondent } from "../lib/api";
import type { Copy } from "../lib/copy";
import { formatSince, useCopy } from "../lib/copy";

interface CasesProps {
  locale: Locale;
  cases: CaseSummary[];
  /** Every conversation the consultant owns. The open case's are listed under it. */
  conversations: ConversationSummary[];
  activeCaseId: number | null;
  activeConversationId: number | null;
  /**
   * Who the consultant thinks it is talking to on the open case — shown as one
   * quiet line so a wrong guess is noticed here rather than mid-interview.
   */
  respondent: Respondent | null;
  /** The parent owns which case is open: it has to load that case's conversation. */
  onSwitch: (id: number) => void;
  /** An empty name is normal; the server names the case from the first conversation. */
  onCreate: (name: string) => void;
  onRename: (id: number, name: string) => void;
  onOpenConversation: (id: number) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: number) => void;
  /** Starts a fresh intake conversation for the open case. */
  onRedoIntake: () => void;
  /**
   * Into the studies for the open case: one question fielded to many people. It
   * lives in this menu rather than in the bar because it is a place you go, not a
   * thing you have to see — the conversation is still the front door.
   */
  onOpenStudies: () => void;
  /** How many studies this case has. Null where nothing has counted them yet. */
  studyCount?: number | null;
  /** Opens the file picker the stage owns. */
  onAttach: () => void;
  /**
   * The chrome controls, rendered here as well as in the bar and shown in exactly
   * one of the two places. On a phone the bar holds the case name, the blob's slot
   * and the fixed notes trigger, and there is not 44px left for three more controls:
   * they used to be 22px tall and the notes trigger was painted on top of two of
   * them. This is where they go instead.
   */
  theme: "dark" | "light";
  onToggleTheme: () => void;
  onChangeLocale: (locale: Locale) => void;
  onSignOut: () => void;
}

/** Endonyms, as in the bar: the label for the other language is that language's own. */
const OTHER_LANGUAGE: Record<Locale, string> = { en: "Русский", ru: "English" };

/**
 * Which name the founder is currently editing. Renaming an existing case and naming a
 * new one are the same input in two places, so they are one piece of state.
 */
type Editing = { kind: "rename"; id: number } | { kind: "new" } | null;

/**
 * The one place in the product where you can be looking at the wrong business without
 * noticing, so the current name is always on screen — quiet, in `.label`, because it is
 * orientation and not navigation. Everything else about the case lives behind a click.
 *
 * Writes nothing itself: switching, creating and renaming are all the parent's, and any
 * failure they hit is written in the status line like every other failure in the
 * product. There is nothing here to indicate progress with, which is why there is no
 * spinner and no toast.
 */
export default function Cases({
  locale,
  cases,
  conversations,
  activeCaseId,
  activeConversationId,
  respondent,
  onSwitch,
  onCreate,
  onRename,
  onOpenConversation,
  onNewConversation,
  onDeleteConversation,
  onRedoIntake,
  onOpenStudies,
  studyCount = null,
  onAttach,
  theme,
  onToggleTheme,
  onChangeLocale,
  onSignOut,
}: CasesProps) {
  const copy = useCopy(locale);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState("");
  /**
   * The sessions inside the open case. The count used to be a number you could not
   * click while `GET /api/conversations` sat there returning the list, so a product
   * whose promise is that it remembers gave no evidence more than one session existed.
   */
  const [sessions, setSessions] = useState(false);
  /** Deleting is two presses in the same row. There is no dialog in this product. */
  const [confirming, setConfirming] = useState<number | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuId = useId();

  const named = useCallback(
    (item: CaseSummary): string => {
      const name = (item.name ?? "").trim();
      return name.length > 0 ? name : copy.caseUntitled;
    },
    [copy.caseUntitled],
  );

  const close = useCallback((restoreFocus: boolean): void => {
    setOpen(false);
    setEditing(null);
    setSessions(false);
    setConfirming(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  /** After an inline edit ends, focus goes back to the row it came from, not to nowhere. */
  const focusRow = useCallback((row: string): void => {
    window.requestAnimationFrame(() => {
      menuRef.current?.querySelector<HTMLElement>(`[data-row="${row}"]`)?.focus();
    });
  }, []);

  // Opening lands on the case you are already in, so the keyboard arrives where the eye
  // does and one arrow key reaches the neighbours.
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const current =
      menu?.querySelector<HTMLElement>('[data-row][aria-current="true"]') ??
      menu?.querySelector<HTMLElement>("[data-row]");
    current?.focus();
  }, [open]);

  useEffect(() => {
    if (editing === null) return;
    const input = inputRef.current;
    input?.focus();
    input?.select();
  }, [editing]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      // Escape backs out one step at a time: the name first, then the menu.
      if (editing !== null) {
        setEditing(null);
        focusRow(editing.kind === "new" ? "new" : String(editing.id));
        return;
      }
      close(true);
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (rootRef.current?.contains(event.target as Node) === true) return;
      close(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [close, editing, focusRow, open]);

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rows = Array.from(menuRef.current?.querySelectorAll<HTMLElement>("[data-row]") ?? []);
    if (rows.length === 0) return;
    event.preventDefault();
    const here = rows.indexOf(document.activeElement as HTMLElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next = here === -1 ? rows[0] : rows[(here + step + rows.length) % rows.length];
    next?.focus();
  };

  // Tabbing out of the last control closes the menu. A focus that has gone nowhere is
  // the input unmounting under its own commit, and must not be read as leaving.
  const onRootBlur = (event: ReactFocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    if (next === null || rootRef.current?.contains(next) === true) return;
    close(false);
  };

  const startRename = (item: CaseSummary): void => {
    setDraft(item.name ?? "");
    setEditing({ kind: "rename", id: item.id });
  };

  const startCreate = (): void => {
    setDraft("");
    setEditing({ kind: "new" });
  };

  const commit = (): void => {
    const name = draft.trim();
    if (editing === null) return;
    if (editing.kind === "new") {
      // Focus goes back to the trigger, which is where the new name is about to appear.
      close(true);
      onCreate(name);
      return;
    }
    const id = editing.id;
    setEditing(null);
    focusRow(String(id));
    if (name.length > 0) onRename(id, name);
  };

  const active = cases.find((item) => item.id === activeCaseId) ?? null;

  return (
    <div className="case" ref={rootRef} onBlur={onRootBlur}>
      {/* Deliberately no aria-haspopup: what opens is a group of buttons, and
          announcing a menu pop-up lands a screen reader somewhere that is not a menu.
          aria-expanded with aria-controls is the disclosure this actually is. */}
      <button
        ref={triggerRef}
        className="case__trigger label"
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown") return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span className="sr-only">{copy.caseCurrent}</span>
        {/* Truncates at about twenty characters in the bar; the title is the only
            place the rest of the name exists outside the menu. */}
        <span className="case__name" title={active === null ? copy.caseUntitled : named(active)}>
          {active === null ? copy.caseUntitled : named(active)}
        </span>
      </button>

      {open && (
        <div
          id={menuId}
          className="case__menu"
          ref={menuRef}
          role="group"
          aria-label={copy.caseListLabel}
          onKeyDown={onMenuKeyDown}
        >
          <ul className="case__list">
            {cases.map((item) => {
              const current = item.id === activeCaseId;
              const renaming = editing !== null && editing.kind === "rename" && editing.id === item.id;
              const mine = current
                ? conversations
                    .filter((session) => session.caseId === item.id)
                    .slice()
                    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
                : [];
              return (
                <li className="case__row" key={item.id}>
                  {renaming ? (
                    <NameForm
                      id={`${menuId}-name`}
                      label={copy.caseNameLabel}
                      placeholder={copy.caseNamePlaceholder}
                      value={draft}
                      inputRef={inputRef}
                      onChange={setDraft}
                      onCommit={commit}
                    />
                  ) : (
                    <>
                      <button
                        className="case__item"
                        type="button"
                        data-row={item.id}
                        aria-current={current}
                        onClick={() => {
                          if (!current) onSwitch(item.id);
                          close(true);
                        }}
                      >
                        <span className="case__item-name">{named(item)}</span>
                        <span className="case__item-meta label">
                          {/* A word, not a colour: the open case has to survive greyscale. */}
                          {current && (
                            <>
                              {copy.caseOpen}
                              <span aria-hidden="true"> · </span>
                            </>
                          )}
                          {/* The open case moves its conversation count into the
                              disclosure below, where it can be pressed. */}
                          {!current && (
                            <>
                              {copy.caseConversationCount(item.conversationCount)}
                              <span aria-hidden="true"> · </span>
                            </>
                          )}
                          {copy.notesCount(item.findingCount)}
                        </span>
                      </button>
                      {/* Renaming is offered where you already are. Anywhere else is a
                          click away and this is not a manager. */}
                      {current && (
                        <button
                          className="case__rename label"
                          type="button"
                          onClick={() => startRename(item)}
                        >
                          {copy.caseRenameCta}
                        </button>
                      )}
                    </>
                  )}
                  {/* The sessions in the open case, behind the count that used to be a
                      number you could not press. */}
                  {current && !renaming && (
                    <button
                      className="case__sessions label"
                      type="button"
                      data-row={`sessions-${item.id}`}
                      aria-expanded={sessions}
                      aria-controls={`${menuId}-sessions`}
                      onClick={() => {
                        setConfirming(null);
                        setSessions((shown) => !shown);
                      }}
                    >
                      {copy.caseConversationCount(mine.length)}
                    </button>
                  )}
                  {current && sessions && !renaming && (
                    <ul
                      id={`${menuId}-sessions`}
                      className="case__sessions-list"
                      aria-label={copy.conversationListLabel}
                    >
                      {mine.map((session) => (
                        <li className="case__session" key={session.id}>
                          {confirming === session.id ? (
                            <div className="case__confirm">
                              <span className="case__confirm-text">{copy.deleteConfirm}</span>
                              <button
                                className="case__danger label"
                                type="button"
                                data-row={`delete-${session.id}`}
                                onClick={() => {
                                  setConfirming(null);
                                  onDeleteConversation(session.id);
                                }}
                              >
                                {copy.deleteYes}
                              </button>
                              <button
                                className="case__rename label"
                                type="button"
                                onClick={() => setConfirming(null)}
                              >
                                {copy.deleteNo}
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                className="case__session-open"
                                type="button"
                                data-row={`conversation-${session.id}`}
                                aria-current={session.id === activeConversationId}
                                onClick={() => {
                                  if (session.id !== activeConversationId) {
                                    onOpenConversation(session.id);
                                  }
                                  close(true);
                                }}
                              >
                                <span className="case__session-name">
                                  {session.title ?? copy.conversationUntitled}
                                </span>
                                <span className="case__session-meta label num">
                                  {formatSince(session.updatedAt, locale)}
                                </span>
                              </button>
                              <button
                                className="case__rename label"
                                type="button"
                                onClick={() => setConfirming(session.id)}
                              >
                                {copy.deleteCta}
                              </button>
                            </>
                          )}
                        </li>
                      ))}
                      <li className="case__session">
                        <button
                          className="case__new case__new--session"
                          type="button"
                          data-row="new-conversation"
                          onClick={() => {
                            close(true);
                            onNewConversation();
                          }}
                        >
                          {copy.conversationNewCta}
                        </button>
                      </li>
                    </ul>
                  )}
                  {/* What the consultant thinks it knows about the person on this
                      case. Orientation, not navigation: the way to correct it is
                      the redo action right underneath. */}
                  {current && !renaming && respondent !== null && (
                    <span className="case__respondent label">
                      {respondentLine(respondent, copy)}
                    </span>
                  )}
                  {current && !renaming && (
                    <>
                      {/* One question, several people, one answer. The other half of
                          the product, one press from the conversation and never in
                          front of it. */}
                      <button
                        className="case__action label"
                        type="button"
                        data-row="studies"
                        onClick={() => {
                          close(false);
                          onOpenStudies();
                        }}
                      >
                        {studyCount === null || studyCount === 0
                          ? copy.studiesLabel
                          : copy.studiesCount(studyCount)}
                      </button>
                      <button
                        className="case__action label"
                        type="button"
                        data-row="attach"
                        onClick={() => {
                          close(false);
                          onAttach();
                        }}
                      >
                        {copy.documentAttachCta}
                      </button>
                      <button
                        className="case__action label"
                        type="button"
                        data-row="redo-intake"
                        onClick={() => {
                          close(false);
                          onRedoIntake();
                        }}
                      >
                        {copy.intakeRedoCta}
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          {editing !== null && editing.kind === "new" ? (
            <NameForm
              id={`${menuId}-new`}
              label={copy.caseNameLabel}
              placeholder={copy.caseNamePlaceholder}
              value={draft}
              inputRef={inputRef}
              onChange={setDraft}
              onCommit={commit}
            />
          ) : (
            <button className="case__new" type="button" data-row="new" onClick={startCreate}>
              {copy.caseNewCta}
            </button>
          )}

          {/* Only ever visible where the top bar cannot hold them. */}
          <div className="case__chrome">
            <button
              className="btn btn--text"
              type="button"
              data-row="theme"
              onClick={onToggleTheme}
            >
              {theme === "dark" ? copy.themeToLight : copy.themeToDark}
            </button>
            <button
              className="btn btn--text"
              type="button"
              data-row="locale"
              onClick={() => onChangeLocale(locale === "en" ? "ru" : "en")}
            >
              {OTHER_LANGUAGE[locale]}
            </button>
            <button
              className="btn btn--text"
              type="button"
              data-row="signout"
              onClick={() => {
                close(false);
                onSignOut();
              }}
            >
              {copy.signOutCta}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The one-liner for who the consultant is talking to: "Head of Sales · Acme" when
 * it knows the title, the role word when it only knows the role. Never a sentence —
 * this is orientation inside a menu, not a profile.
 */
function respondentLine(respondent: Respondent, copy: Copy): string {
  const detail = (respondent.roleDetail ?? "").trim();
  const role = detail.length > 0 ? detail : copy.respondentRole[respondent.role];
  const company = (respondent.company ?? "").trim();
  return company.length > 0 ? `${role} · ${company}` : role;
}

interface NameFormProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onChange: (value: string) => void;
  onCommit: () => void;
}

/** A real form, so Return commits without a key handler and without a submit button. */
function NameForm({ id, label, placeholder, value, inputRef, onChange, onCommit }: NameFormProps) {
  return (
    <form
      className="case__form"
      onSubmit={(event) => {
        event.preventDefault();
        onCommit();
      }}
    >
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        ref={inputRef}
        className="input case__input"
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => onChange(event.target.value)}
      />
    </form>
  );
}
